import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Observable, Subject } from 'rxjs'
import { SecurityDeposit, SecurityDepositStatusSummaryResponse } from './security-deposits.model'
import { environment } from '../../../environments/environment'

export interface SecurityDepositsResponseMetadata {
  total?: number
  page?: number
}

export interface SecurityDepositsResponse {
  data: SecurityDeposit[]
  metadata: SecurityDepositsResponseMetadata
}

export interface CreateSecurityDepositPayload {
  inquiry_id: string
  rental_id: string
  channel?: string
  amount: number
  currency: string
}

export interface SecurityDepositLog {
  created: number
  security_deposit_id: string
  message: string
  account?: number
  gateway_id?: string
  pkey: string
  status?: number
  channel?: string
  rental_id?: string
  txn_id?: string
  card?: {
    id?: string
  }
  total?: number
  service?: string
  inquiry_id?: string
}

@Injectable({
  providedIn: 'root',
})
export class SecurityDepositsService {
  private readonly baseUrl = '@api/securitydeposit'
  private readonly summaryRefresh$ = new Subject<void>()

  constructor(private http: HttpClient) {}

  // Deposits API (new endpoint)
  // Uses POST /securitydeposit/find as per example http file
  loadDeposits(body?: Record<string, any>): Observable<SecurityDepositsResponse> {
    const payload = body || {}
    return this.http.post<SecurityDepositsResponse>(`${this.baseUrl}/find`, payload)
  }

  cancelDeposit(depositId: string): Observable<SecurityDeposit> {
    return this.http.put<SecurityDeposit>(`${this.baseUrl}/${depositId}/cancel`, {})
  }

  captureDeposit(depositId: string): Observable<SecurityDeposit> {
    return this.http.put<SecurityDeposit>(`${this.baseUrl}/${depositId}/capture`, {})
  }

  refundDeposit(depositId: string): Observable<SecurityDeposit> {
    return this.http.put<SecurityDeposit>(`${this.baseUrl}/${depositId}/refund`, {})
  }

  sendDeposit(depositId: string): Observable<SecurityDeposit> {
    return this.http.put<SecurityDeposit>(`${this.baseUrl}/${depositId}/send`, {})
  }

  createDeposit(payload: CreateSecurityDepositPayload): Observable<SecurityDeposit> {
    return this.http.post<SecurityDeposit>(`${this.baseUrl}`, payload)
  }

  loadDepositStatusSummary(): Observable<SecurityDepositStatusSummaryResponse> {
    return this.http.get<SecurityDepositStatusSummaryResponse>(`${this.baseUrl}/stats`)
  }

  get summaryRefreshTrigger$(): Observable<void> {
    return this.summaryRefresh$.asObservable()
  }

  notifySummaryChanged(): void {
    this.summaryRefresh$.next()
  }

  viewAsGuest(deposit: SecurityDeposit): boolean {
    const publicId = this.getPublicId(deposit)
    if (!publicId || typeof window === 'undefined') return false

    const url = this.buildGuestUrl(publicId)
    window.open(url, '_blank', 'width=1100, height=1000')
    return true
  }

  updateDepositGateway(depositId: string, gatewayId: string): Observable<SecurityDeposit> {
    return this.http.put<SecurityDeposit>(`${this.baseUrl}/${depositId}/gateway`, { gateway_id: gatewayId })
  }

  loadDepositLogs(depositId: string): Observable<SecurityDepositLog[]> {
    return this.http.get<SecurityDepositLog[]>(`${this.baseUrl}/${depositId}/logs`)
  }

  private getPublicId(deposit: SecurityDeposit): string | null {
    if (!deposit) return null
    return deposit.public_id || null
  }

  private buildGuestUrl(publicId: string): string {
    const baseFromConfig = environment.config?.invoiceBaseUrl
    const isProduction = environment.environment === 'production'
    const fallbackBase = isProduction ? 'https://invoices.advance.cm/' : 'http://stage.invoices.advance.cm/'

    const baseUrl = (baseFromConfig || fallbackBase).replace(/\/$/, '')
    return `${baseUrl}/security-deposit/${publicId}`
  }
}
